// ============================================
// CONSOMMATION DE L'IA — APPEL ET MISE EN FORME
// ============================================
// Route (backend, controllers/consommation-ia.controller.ts) :
//   GET /admin/ai/consommation?periode=jour|7j|30j|mois|personnalise&du=&au=
//   ADMIN et SUPERVISOR seulement (403 sinon), 400 période invalide.
// Les dates sont celles de Paris (AAAA-MM-JJ). Le coût vaut null quand un
// modèle n'a pas de tarif dans le réglage « ia.tarifs » : « non renseigné »,
// jamais 0.

import { fetchWithAuth } from '../../services/api';

export type FonctionIA =
  | 'brouillon' | 'synthese' | 'pieces_jointes' | 'tri_courrier' | 'assistant_client'
  | 'assistant_espace_client' | 'assistant_tableau_de_bord' | 'generation_libre' | 'test_parametres' | 'autre';

export type CodePeriode = 'jour' | '7j' | '30j' | 'mois' | 'personnalise';

export interface SommesConsommation {
  appels: number;
  echecs: number;
  jetonsEntree: number;
  jetonsSortie: number;
  jetonsRaisonnement: number;
  jetonsCache: number;
  jetonsTotal: number;
  coutEstime: number | null;
}

export interface RapportConsommation {
  periode: { code: CodePeriode; du: string; au: string; jours: number };
  tarifs: { renseignes: boolean; erreur: string | null };
  totaux: SommesConsommation & { modelesSansTarif: string[] };
  parFonction: Array<SommesConsommation & { fonction: FonctionIA; part: number }>;
  parModele: Array<SommesConsommation & { fournisseur: string; modele: string; tarifRenseigne: boolean }>;
  parJour: Array<{ jour: string; appels: number; jetonsTotal: number; coutEstime: number | null }>;
  topDossiers: Array<{ ticketId: string; ticketRef: string | null; ticketNumber: number | null; titre: string | null; appels: number; jetonsTotal: number }>;
  moyenneJetonsBrouillon: number | null;
}

export const LIBELLES_FONCTION: Record<FonctionIA, string> = {
  brouillon: 'Brouillon du conseiller',
  synthese: 'Synthèse du dossier',
  pieces_jointes: 'Lecture des pièces jointes',
  tri_courrier: 'Tri des e-mails',
  assistant_client: 'Assistant client automatique',
  assistant_espace_client: 'Assistant de l’espace client',
  assistant_tableau_de_bord: 'Assistant du tableau de bord',
  generation_libre: 'Génération libre',
  test_parametres: 'Test des paramètres',
  autre: 'Autre',
};

export const PERIODES: Array<{ code: CodePeriode; libelle: string }> = [
  { code: 'jour', libelle: 'Aujourd’hui' },
  { code: '7j', libelle: '7 derniers jours' },
  { code: '30j', libelle: '30 derniers jours' },
  { code: 'mois', libelle: 'Mois en cours' },
  { code: 'personnalise', libelle: 'Période choisie' },
];

export function libelleFonction(f: string): string {
  return LIBELLES_FONCTION[f as FonctionIA] ?? f;
}

// Espace insécable fine (séparateur des milliers en français) remplacée par
// une espace insécable ordinaire : même rendu dans toutes les polices.
const NOMBRE = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
const DOLLARS = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 1234567 → « 1 234 567 ». */
export function formatNombre(n: number): string {
  return NOMBRE.format(Math.round(n)).replace(/[  ]/g, ' ');
}

/** Coût en dollars, 2 décimales : « 12,35 $ » ; « < 0,01 $ » pour un coût non nul plus petit. */
export function formatDollars(n: number): string {
  if (n > 0 && n < 0.005) return '< 0,01 $';
  return `${DOLLARS.format(n).replace(/[  ]/g, ' ')} $`;
}

/** Coût, ou « non renseigné » quand un tarif manque. */
export function formatCout(n: number | null): string {
  return n === null ? 'non renseigné' : formatDollars(n);
}

/** Pourcentage arrondi : 0,256 → « 26 % » ; une part non nulle sous 1 % → « < 1 % ». */
export function formatPart(part: number): string {
  if (part > 0 && part < 0.005) return '< 1 %';
  return `${Math.round(part * 100)} %`;
}

/** « 2026-10-19 » → « 19/10 ». */
export function jourCourt(jour: string): string {
  const [, m, j] = jour.split('-');
  return `${j}/${m}`;
}

/** « 2026-10-19 » → « 19/10/2026 ». */
export function jourLong(jour: string): string {
  const [a, m, j] = jour.split('-');
  return `${j}/${m}/${a}`;
}

/** Référence lisible d'un dossier : 26-0042, sinon #42, sinon « dossier supprimé ». */
export function referenceDossier(d: { ticketRef: string | null; ticketNumber: number | null }): string {
  if (d.ticketRef) return d.ticketRef;
  if (d.ticketNumber !== null) return `#${d.ticketNumber}`;
  return 'Dossier supprimé';
}

export interface DemandePeriode {
  periode: CodePeriode;
  du?: string;
  au?: string;
}

export function requetePeriode(d: DemandePeriode): string {
  const p = new URLSearchParams({ periode: d.periode });
  if (d.periode === 'personnalise') {
    if (d.du) p.set('du', d.du);
    if (d.au) p.set('au', d.au);
  }
  return p.toString();
}

export async function chargerConsommation(d: DemandePeriode): Promise<RapportConsommation> {
  return fetchWithAuth<RapportConsommation>(`/admin/ai/consommation?${requetePeriode(d)}`);
}
